// src/components/usage/usage-tables.tsx
import { GridColDef } from "@mui/x-data-grid-premium";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import CustomTable from "components/shared/table";
import { Alert } from "components/ui/alert";
import { useIdentity } from "contexts/identity-context";
import { AppTotal } from "contexts/models";
import { UsageTableProps } from "./types";
import { resolveAppName } from "./utils-usage";

const cols: GridColDef[] = [
  { field: "keyName", headerName: "API Key", width: 200 },
  { field: "addresses", headerName: "Addresses API", width: 110 },
  { field: "buildings", headerName: "Buildings API", width: 110 },
  { field: "batches", headerName: "Batches API", width: 110 },
  { field: "maps", headerName: "Maps API", width: 110 },
  { field: "landparcels", headerName: "Land Parcels API", width: 110 },
  { field: "predictive", headerName: "Predictive API", width: 110 },
  { field: "esri", headerName: "Esri Compatible Locator", width: 110 },
  { field: "clip", headerName: "Clip", width: 80 },
  {
    field: "adminbounds",
    headerName: "Administrative Boundaries API",
    width: 150,
  },
];

const parseDateString = (dateString: string) => {
  const [day, month, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
};

const modifyColumns = (columns: any): any => {
  return columns.map((column: any) => {
    if (column.field === "Date") {
      column = {
        ...column,
        sortComparator: (v1: string, v2: string) => {
          const date1 = parseDateString(v1);
          const date2 = parseDateString(v2);
          return date1.getTime() - date2.getTime();
        },
      };
    }
    if (!column.valueGetter) {
      column = {
        ...column,
        valueGetter: (params: any) => params ?? 0,
      };
    }
    return column;
  });
};

const totalOrZero = (v: AppTotal | undefined) => {
  if (v !== undefined) {
    return Number(v.totalCredits.toFixed(1));
  }
  return 0;
};

const buildRowData = (apiKeysQuery: any, usageQuery: any) => {
  let keyNameMapping: Record<string, string> = {};
  apiKeysQuery.data.forEach(function (key: any) {
    const keyName: string = key.name;
    const keyDisplayName: string = key.displayName ? key.displayName : key.name;
    keyNameMapping[keyName] = keyDisplayName;
  });

  const rows: any = [];

  usageQuery.data?.appProductTotals.forEach((app: any, index: any) => {
    if (app.totalUsage.length === 0 && keyNameMapping[app.name] === undefined) {
      return;
    }

    rows.push({
      id: app.name,
      keyName: resolveAppName(app.name, keyNameMapping),
      predictive: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("predictive")),
      ),
      addresses: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("addresses")),
      ),
      landparcels: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("land-parcels")),
      ),
      buildings: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("buildings")),
      ),
      batches: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("batches")),
      ),
      adminbounds: totalOrZero(
        app.totalUsage.find((x: any) =>
          x.product.includes("administrative-boundaries"),
        ),
      ),
      maps: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("maps")),
      ),
      esri: totalOrZero(
        app.totalUsage.find((x: any) =>
          x.product.includes("esri-compatible-locator"),
        ),
      ),
      clip: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("clip")),
      ),
    });
  });

  return rows;
};

export const UsageTable = (props: UsageTableProps) => {
  const [identityState] = useIdentity();

  const usageQuery = props.usageQuery;
  const apiKeysQuery = props.apiKeysQuery;

  return (
    <div className="flex flex-col w-full mb-8">
      <ReactQueryErrorWrapper
        queries={[usageQuery, apiKeysQuery]}
        mutations={[]}
      />

      {props.isSuccess && (
        <CustomTable
          rows={buildRowData(apiKeysQuery, usageQuery)}
          cols={cols}
          isLoading={props.isLoading}
          overlayText={"No usage data"}
          toolbar={true}
          initialState={{ pinnedColumns: { left: ["keyName"] } }}
        />
      )}
    </div>
  );
};

export const UsageCreditByTimeTable = (props: UsageTableProps) => {
  const usageQuery = props.usageQuery;

  function getRowId(row: any) {
    return row.Date;
  }

  return (
    <div className="flex flex-col w-full">
      {props.isError && (
        <Alert variant="destructive">
          An error occurred while fetching the usage data.
        </Alert>
      )}

      {props.isSuccess && (
        <CustomTable
          rows={usageQuery.data?.appCreditsData.rows}
          cols={modifyColumns(usageQuery.data?.appCreditsData.columns)}
          isLoading={props.isLoading}
          getRowId={getRowId}
          columnGroupingModel={usageQuery.data?.appCreditsData.group}
          overlayText={"No usage data"}
          toolbar={true}
          initialState={{ pinnedColumns: { left: ["keyName"] } }}
        />
      )}
    </div>
  );
};
